/**
 * Sprint 24 — `/connections` unified surface.
 *
 * Replaces WrappedApisDashboardPage. Lists BOTH MCP connections and Wrapped APIs
 * as first-class equals (type chip per row, shared status pills). The Add button
 * opens a two-step picker (AddConnectionMenu): First-party (24.5 teaser), Custom
 * MCP (RegisterMcpServerModal), or Custom Wrapped (the existing ConnectApiWizard).
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { EmptyState, showToast } from "../components/primitives";
import { ConnectionRow } from "../components/connections/ConnectionRow";
import { HiringBanner, type HiringBannerMissing } from "../components/connections/HiringBanner";
import { usePreviewAgentTemplate, type AvailabilityStatus } from "../hooks/use-agent-templates";
import {
  kindIsInstalled,
  parseTemplateIdFromReturnTo,
  type MatchableConnection,
} from "../components/hire-wizard/hire-template-handoff";
import {
  AddConnectionMenu,
  type AddConnectionPath,
} from "../components/connections/AddConnectionMenu";
import { RegisterMcpServerModal } from "../components/connections/RegisterMcpServerModal";
import { AddWrappedApiModal } from "../components/wrapped/AddWrappedApiModal";
import { ConnectApiWizard } from "../components/wrapped/ConnectApiWizard";
import { InstallWizardModal } from "../components/connections/install-wizard/InstallWizardModal";
import { useUnifiedConnections, type UnifiedConnection } from "../../hooks/use-connections";
import {
  type FirstPartyTemplate,
  useFirstPartyTemplateMap,
} from "../../hooks/use-first-party-templates";
import type { WizardSeed } from "../hooks/use-wrapped-api-wizard";
import { useAuth } from "../../lib/hooks/use-auth";
import { useChatScope } from "../hooks/use-chat-scope";

type FilterKey = "all" | "mcp" | "wrapped" | "first-party";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "mcp", label: "MCP" },
  { key: "wrapped", label: "Wrapped" },
  { key: "first-party", label: "First-party" },
];

function matchesFilter(c: UnifiedConnection, f: FilterKey): boolean {
  if (f === "all") return true;
  if (f === "first-party") return !!c.first_party_template_slug;
  return c.kind === f;
}

function detailPath(c: UnifiedConnection): string {
  return c.kind === "mcp"
    ? `/connections/m/${c.connection_id}`
    : `/connections/w/${c.connection_id}`;
}

export function ConnectionsDashboardPage() {
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";
  const connections = useUnifiedConnections();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<FilterKey>("all");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mcpModalOpen, setMcpModalOpen] = useState(false);
  // Sprint 24.15.2 — when the MCP register modal is opened from a hire's
  // "install this kind" CTA, seed the connection name to the equipment kind.
  const [mcpSeedName, setMcpSeedName] = useState<string | undefined>(undefined);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSeed, setWizardSeed] = useState<WizardSeed | undefined>(undefined);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSeed, setManualSeed] = useState<{ name?: string; slug?: string; url?: string } | undefined>(undefined);
  // Sprint 24.5 — first-party install wizard target (null = closed).
  const [installTemplate, setInstallTemplate] = useState<FirstPartyTemplate | null>(null);
  const templateMap = useFirstPartyTemplateMap();

  // Sprint 22 chat scope (Q22) — preserved from the wrapped dashboard.
  useChatScope({ kind: "workspace", label: "Connections" }, null, []);

  // Sprint 16d deep-link — Cmd-K "Connect new API" still opens the wrapped wizard.
  useEffect(() => {
    if (searchParams.get("wizard") === "1") {
      setWizardSeed(undefined);
      setWizardOpen(true);
      searchParams.delete("wizard");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  function handleSelectPath(path: AddConnectionPath) {
    setMenuOpen(false);
    if (path === "custom-mcp") {
      setMcpModalOpen(true);
      return;
    }
    setWizardSeed(undefined);
    setWizardOpen(true);
  }

  function handleSelectTemplate(template: FirstPartyTemplate) {
    setMenuOpen(false);
    setInstallTemplate(template);
  }

  const rows = connections.data ?? [];
  const filtered = useMemo(() => rows.filter((c) => matchesFilter(c, filter)), [rows, filter]);
  const counts = useMemo(
    () => ({
      all: rows.length,
      mcp: rows.filter((c) => c.kind === "mcp").length,
      wrapped: rows.filter((c) => c.kind === "wrapped").length,
      "first-party": rows.filter((c) => !!c.first_party_template_slug).length,
    }),
    [rows],
  );

  // -------------------------------------------------------------------------
  // Sprint 24.15.2 — hire ↔ Connections handoff (`?missing=…&return_to=…`).
  // -------------------------------------------------------------------------
  const returnTo = searchParams.get("return_to");
  const missingParam = searchParams.get("missing");
  const missingKinds = useMemo(
    () => (missingParam ? missingParam.split(",").map((s) => s.trim()).filter(Boolean) : []),
    [missingParam],
  );
  const hiringActive = missingKinds.length > 0 && !!returnTo;
  const hiringTemplateId = parseTemplateIdFromReturnTo(returnTo);
  const hiringTemplate = usePreviewAgentTemplate(hiringActive ? hiringTemplateId : null);
  const templateName = hiringTemplate.data?.name ?? "your new hire";

  // Connections in the matcher shape (server_name is MCP-only).
  const matchable: MatchableConnection[] = useMemo(
    () =>
      rows.map((c) => ({
        connection_id: c.connection_id,
        name: c.name,
        server_name: c.kind === "mcp" ? c.server_name : null,
        first_party_template_slug: c.first_party_template_slug,
      })),
    [rows],
  );

  // Resolve each missing kind's availability status from the template (default to
  // generic-mcp — installable via custom MCP — when the template/kind is unknown).
  const hiringMissing: HiringBannerMissing[] = useMemo(() => {
    const equip = hiringTemplate.data?.default_equipment ?? [];
    return missingKinds.map((kind) => {
      const match = equip.find((e) => e.kind === kind);
      return {
        kind,
        status: (match?.availability_status ?? "generic-mcp") as AvailabilityStatus,
        note: match?.availability_note ?? null,
        installed: kindIsInstalled(kind, matchable),
      };
    });
  }, [missingKinds, hiringTemplate.data, matchable]);

  function handleInstallKind(kind: string, status: AvailabilityStatus) {
    if (status === "first-party") {
      // Prefer a matching first-party template; fall back to custom MCP.
      const norm = (s: string) => s.replace(/[^a-z0-9]/gi, "").toLowerCase();
      const fpTemplate = Object.values(templateMap.map).find((t) => norm(t.slug) === norm(kind));
      if (fpTemplate) {
        setInstallTemplate(fpTemplate);
        return;
      }
    }
    if (status === "wrapped-api") {
      setWizardSeed(undefined);
      setWizardOpen(true);
      return;
    }
    // generic-mcp (and unmatched first-party) → custom MCP register, name pre-filled.
    setMcpSeedName(kind);
    setMcpModalOpen(true);
  }

  // Auto-resume (S24.15.2.13): once a NEW connection appears during this visit
  // and every installable missing kind is satisfied, offer to continue the hire.
  const installableKinds = useMemo(
    () => hiringMissing.filter((m) => m.status !== "coming-soon").map((m) => m.kind),
    [hiringMissing],
  );
  const allInstallableSatisfied =
    installableKinds.length > 0 && installableKinds.every((k) => kindIsInstalled(k, matchable));
  const prevCountRef = useRef<number | null>(null);
  const resumeFiredRef = useRef(false);
  useEffect(() => {
    const count = rows.length;
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (!hiringActive || !returnTo || resumeFiredRef.current) return;
    // Only fire on a transition (a connection was added this visit), not on mount.
    if (prev !== null && count > prev && allInstallableSatisfied) {
      resumeFiredRef.current = true;
      showToast(
        `All required connections installed — continue hiring ${templateName}?`,
        "success",
        false,
        { label: "Continue", onClick: () => navigate(returnTo) },
        10_000,
      );
    }
  }, [rows.length, allInstallableSatisfied, hiringActive, returnTo, templateName, navigate]);

  return (
    <div style={{ maxWidth: 840 }}>
      {hiringActive && (
        <HiringBanner
          templateName={templateName}
          missing={hiringMissing}
          onInstallKind={handleInstallKind}
          onReturn={() => navigate(returnTo!)}
        />
      )}
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Connections</h1>
          <div style={subtitle}>
            Federate MCP servers and wrap OpenAPI services — every connection your workforce can use, in one place.
          </div>
        </div>
        {isOwner && (
          <button type="button" onClick={() => setMenuOpen(true)} style={addButton}>
            <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
            Add connection
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <div style={filterRow}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              style={filter === f.key ? filterChipActive : filterChip}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span style={filterCount}>{counts[f.key]}</span>
            </button>
          ))}
        </div>
      )}

      {connections.isLoading ? (
        <div style={list}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={skeletonRow} />
          ))}
        </div>
      ) : connections.error ? (
        <div style={errorCard}>
          <EmptyState>Failed to load connections.</EmptyState>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button onClick={() => connections.refetch()} style={retryBtn}>
              Retry
            </button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div style={emptyWrap}>
          <div style={emptyTitle}>No connections yet</div>
          <div style={emptyTagline}>
            Connect an MCP server or wrap an API to give your hires new abilities.
          </div>
          {isOwner ? (
            <>
              <button type="button" onClick={() => setMenuOpen(true)} style={{ ...addButton, marginTop: 4 }}>
                <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
                Add connection
              </button>
              <div style={starterRow}>
                {["HubSpot", "Linear", "Internal API"].map((s) => (
                  <button key={s} type="button" style={starterChip} onClick={() => setMenuOpen(true)}>
                    {s}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div style={emptyTagline}>An owner can add a connection to this workspace.</div>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div style={errorCard}>
          <EmptyState>No {filter} connections.</EmptyState>
        </div>
      ) : (
        <div style={list}>
          {filtered.map((c) => (
            <ConnectionRow
              key={`${c.kind}:${c.connection_id}`}
              connection={c}
              accentColor={
                c.first_party_template_slug
                  ? templateMap.map[c.first_party_template_slug]?.brand.accent_border
                  : undefined
              }
              onClick={() => navigate(detailPath(c))}
            />
          ))}
        </div>
      )}

      <AddConnectionMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSelectPath={handleSelectPath}
        onSelectTemplate={handleSelectTemplate}
      />

      {installTemplate && (
        <InstallWizardModal
          template={installTemplate}
          onComplete={(result) => {
            setInstallTemplate(null);
            connections.refetch();
            // Stay on-page during a hiring handoff so auto-resume can fire.
            if (hiringActive) return;
            if (result.connection_id) {
              navigate(
                result.connection_kind === "mcp"
                  ? `/connections/m/${result.connection_id}`
                  : `/connections/w/${result.connection_id}`,
              );
            }
          }}
          onCancel={() => {
            setInstallTemplate(null);
            setMenuOpen(true);
          }}
        />
      )}

      <RegisterMcpServerModal
        open={mcpModalOpen}
        seedName={mcpSeedName}
        onClose={() => {
          setMcpModalOpen(false);
          setMcpSeedName(undefined);
        }}
        onCreated={(id) => {
          setMcpModalOpen(false);
          setMcpSeedName(undefined);
          connections.refetch();
          // During a hiring handoff, stay on this page so the auto-resume toast
          // can fire; otherwise jump to the new connection's detail page.
          if (hiringActive) return;
          navigate(`/connections/m/${id}`);
        }}
      />

      <AddWrappedApiModal
        open={manualOpen}
        onOpenChange={setManualOpen}
        onCreated={(slug) => navigate(`/connections/w/${slug}`)}
        seed={manualSeed}
        onUseWizard={() => {
          setManualOpen(false);
          setWizardSeed(manualSeed ? { specUrl: manualSeed.url, name: manualSeed.name, slug: manualSeed.slug } : undefined);
          setWizardOpen(true);
        }}
      />

      <ConnectApiWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        seed={wizardSeed}
        onDone={(slug) => {
          setWizardOpen(false);
          connections.refetch();
          // Stay on-page during a hiring handoff so auto-resume can fire.
          if (hiringActive) return;
          navigate(`/connections/w/${slug}`);
        }}
        onConfigureManually={(s) => {
          setManualSeed({ name: s.name, slug: s.slug, url: s.url });
          setManualOpen(true);
        }}
      />
    </div>
  );
}

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  marginBottom: 16,
};

const subtitle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  marginTop: 2,
  maxWidth: 560,
};

const addButton: CSSProperties = {
  fontSize: 12,
  padding: "6px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const filterRow: CSSProperties = { display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" };

const filterChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 999,
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const filterChipActive: CSSProperties = {
  ...filterChip,
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  border: "0.5px solid var(--color-text-primary)",
};

const filterCount: CSSProperties = { fontSize: 10, opacity: 0.7 };

const list: CSSProperties = { display: "flex", flexDirection: "column", gap: 8 };

const skeletonRow: CSSProperties = {
  height: 58,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
  opacity: 0.6,
};

const errorCard: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: 24,
};

const emptyWrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  textAlign: "center",
  padding: "56px 24px",
};

const emptyTitle: CSSProperties = { fontSize: 16, fontWeight: 500, color: "var(--color-text-primary)" };
const emptyTagline: CSSProperties = { fontSize: 13, color: "var(--color-text-secondary)", maxWidth: 380 };

const starterRow: CSSProperties = { display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", justifyContent: "center" };

const starterChip: CSSProperties = {
  fontSize: 12,
  padding: "5px 12px",
  borderRadius: 999,
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const retryBtn: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
